import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { fetchSavedSearches, saveSearch } from "../services/savedSearchService";

const toErrorMessage = (err, fallback) => {
  if (!err) return fallback;
  if (typeof err === "string") return err;
  return err?.message || fallback;
};

export const fetchSavedSearchesAsync = createAsyncThunk(
  "savedSearch/fetchSavedSearches",
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchSavedSearches();

      if (Array.isArray(response)) return response;
      if (Array.isArray(response?.items)) return response.items;

      return [];
    } catch (error) {
      return rejectWithValue(toErrorMessage(error, "Failed to fetch saved searches."));
    }
  }
);

export const saveSearchAsync = createAsyncThunk(
  "savedSearch/saveSearch",
  async (searchDetails, { rejectWithValue }) => {
    try {
      const response = await saveSearch(searchDetails);

      // backend might wrap: { item: {...} } or return object directly
      return response?.item || response;
    } catch (error) {
      return rejectWithValue(toErrorMessage(error, "Failed to save the search."));
    }
  }
);

const initialState = {
  savedSearches: [],
  loading: false,
  error: null,
};

const savedSearchSlice = createSlice({
  name: "savedSearch",
  initialState,
  reducers: {
    clearSavedSearchError(state) {
      state.error = null;
    },
    resetSavedSearchesState() {
      return { ...initialState, savedSearches: [] };
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchSavedSearchesAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSavedSearchesAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.savedSearches = action.payload || [];
      })
      .addCase(fetchSavedSearchesAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch saved searches.";
      })

      // Save
      .addCase(saveSearchAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveSearchAsync.fulfilled, (state, action) => {
        state.loading = false;
        const saved = action.payload;

        if (!saved) return;

        // Optional: dedupe by id if present
        const savedId = saved.id || saved.searchId || saved._id;
        if (savedId) {
          state.savedSearches = state.savedSearches.filter((s) => {
            const id = s.id || s.searchId || s._id;
            return id !== savedId;
          });
        }

        state.savedSearches.push(saved);
      })
      .addCase(saveSearchAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to save the search.";
      });
  },
});

export const { clearSavedSearchError, resetSavedSearchesState } = savedSearchSlice.actions;

// Safe selectors
export const selectSavedSearches = (s) => s.savedSearch?.savedSearches || [];
export const selectSavedSearchLoading = (s) => s.savedSearch?.loading || false;
export const selectSavedSearchError = (s) => s.savedSearch?.error || null;

export default savedSearchSlice.reducer;
